import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Receipt, ShoppingCart, Truck, FileText,
  ArrowCounterClockwise, TrendUp, TrendDown, Wallet,
  ArrowUp, ArrowDown, Warning, CheckCircle, ArrowRight
} from '@phosphor-icons/react';

import { API } from '../config/api';

const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function AccountingDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/accounting/stats`);
      setStats(data || {})
    } catch (err) {
      console.error('Error fetching accounting stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  if (loading) {
    return <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>;
  }

  if (!stats) return null;

  const alerts = stats.alerts || {};
  const hasOverdue = alerts.overdue_invoices?.count > 0;
  const hasPassive = alerts.passive_to_register?.count > 0;
  const hasAlerts = hasOverdue || hasPassive;

  const getDaysOverdue = (dueDateStr) => {
    const due = new Date(dueDateStr);
    const now = new Date();
    return Math.floor((now - due) / (1000 * 60 * 60 * 24));
  };

  const formatDateIt = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  const maxBar = Math.max(
    ...(stats.andamento_mensile || []).map(m => Math.max(m.fatturato, m.acquisti)),
    1
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="accounting-dashboard-title">
          <Wallet size={28} weight="duotone" className="text-[#0033A0]" />
          {t('accountingDashboard.title')}
        </h1>
        <p className="text-[#4A5568] mt-1">{t('accountingDashboard.subtitle')}</p>
      </div>

      {/* Alerts Section */}
      {hasAlerts ? (
        <div className="space-y-3" data-testid="alerts-section">
          {hasOverdue && (
            <div className="rounded-xl border-l-4 border-l-red-500 bg-red-50 border border-red-200 p-4" data-testid="alert-overdue-invoices">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Warning size={22} weight="fill" className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-800 text-sm">{t('accountingDashboard.overdueTitle')}</h3>
                    <p className="text-red-700 text-xs mt-0.5">
                      {t('accountingDashboard.overdueDesc', {
                        count: alerts.overdue_invoices.count,
                        total: formatCurrency(alerts.overdue_invoices.total)
                      })}
                    </p>
                    {alerts.overdue_invoices.items?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {(alerts.overdue_invoices?.items || []).map((inv) => (
                          <div key={inv.id} className="flex items-center gap-2 text-xs text-red-700 bg-red-100/60 rounded-md px-2 py-1">
                            <span className="font-mono font-medium">{inv.invoice_number}</span>
                            <span className="text-red-500">-</span>
                            <span className="truncate">{inv.client_name}</span>
                            <span className="ml-auto font-semibold whitespace-nowrap">{formatCurrency(inv.total)}</span>
                            <span className="text-red-400 whitespace-nowrap text-[10px]">
                              ({t('accountingDashboard.overdueDays', { days: getDaysOverdue(inv.due_date) })})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/fatture')}
                  className="flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
                  data-testid="alert-overdue-action"
                >
                  {t('accountingDashboard.overdueAction')}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {hasPassive && (
            <div className="rounded-xl border-l-4 border-l-amber-500 bg-amber-50 border border-amber-200 p-4" data-testid="alert-passive-invoices">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Warning size={22} weight="fill" className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 text-sm">{t('accountingDashboard.passiveTitle')}</h3>
                    <p className="text-amber-700 text-xs mt-0.5">
                      {t('accountingDashboard.passiveDesc', {
                        count: alerts.passive_to_register.count
                      })}
                    </p>
                    {alerts.passive_to_register.items?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {(alerts.passive_to_register?.items || []).map((inv) => (
                          <div key={inv.id} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-100/60 rounded-md px-2 py-1">
                            <span className="font-medium truncate">{inv.sender_name || 'Fornitore'}</span>
                            {inv.date && <span className="text-amber-500 text-[10px]">{formatDateIt(inv.date)}</span>}
                            {inv.total && <span className="ml-auto font-semibold whitespace-nowrap">{formatCurrency(inv.total)}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/fatture-passive')}
                  className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
                  data-testid="alert-passive-action"
                >
                  {t('accountingDashboard.passiveAction')}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl" data-testid="no-alerts">
          <CheckCircle size={20} weight="fill" className="text-green-600" />
          <span className="text-sm text-green-700 font-medium">{t('accountingDashboard.noAlerts')}</span>
        </div>
      )}

      {/* Main KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-[#E2E8F0]" data-testid="kpi-fatturato-anno">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendUp size={18} className="text-blue-600" />
              </div>
              <span className="text-xs text-[#4A5568] font-medium">{t('accountingDashboard.invoicedYear')}</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1A]">{formatCurrency(stats.fatturato.anno)}</p>
            <p className="text-[10px] text-[#4A5568] mt-1">{stats.fatturato.fatture_anno} {t('accountingDashboard.invoicesLabel')}</p>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="kpi-acquisti-anno">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendDown size={18} className="text-red-600" />
              </div>
              <span className="text-xs text-[#4A5568] font-medium">{t('accountingDashboard.purchasesYear')}</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1A]">{formatCurrency(stats.acquisti.anno)}</p>
            <p className="text-[10px] text-[#4A5568] mt-1">{stats.acquisti.acquisti_anno} {t('accountingDashboard.purchasesLabel')}</p>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="kpi-margine">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stats.bilancio.margine_anno >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {stats.bilancio.margine_anno >= 0 ?
                  <ArrowUp size={18} className="text-green-600" /> :
                  <ArrowDown size={18} className="text-red-600" />
                }
              </div>
              <span className="text-xs text-[#4A5568] font-medium">{t('accountingDashboard.marginYear')}</span>
            </div>
            <p className={`text-xl font-bold ${stats.bilancio.margine_anno >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(stats.bilancio.margine_anno)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0]" data-testid="kpi-da-incassare">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Wallet size={18} className="text-amber-600" />
              </div>
              <span className="text-xs text-[#4A5568] font-medium">{t('accountingDashboard.toCollect')}</span>
            </div>
            <p className="text-xl font-bold text-amber-700">{formatCurrency(stats.fatturato.da_incassare)}</p>
            <p className="text-[10px] text-red-500 mt-1">{t('accountingDashboard.toPay')}: {formatCurrency(stats.acquisti.da_pagare)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart + Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly trend chart */}
        <Card className="border-[#E2E8F0] lg:col-span-2" data-testid="monthly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
              {t('accountingDashboard.monthlyTrend')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-[200px]">
              {(stats.andamento_mensile || []).map((m, idx) => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end">
                  <div className="w-full flex gap-[1px] items-end flex-1">
                    <div
                      className="flex-1 bg-blue-400 rounded-t-sm transition-all min-h-[2px]"
                      style={{ height: `${(m.fatturato / maxBar) * 100}%` }}
                      title={`Fatturato: ${formatCurrency(m.fatturato)}`}
                    />
                    <div
                      className="flex-1 bg-red-400 rounded-t-sm transition-all min-h-[2px]"
                      style={{ height: `${(m.acquisti / maxBar) * 100}%` }}
                      title={`Acquisti: ${formatCurrency(m.acquisti)}`}
                    />
                  </div>
                  <span className="text-[9px] text-[#4A5568]">{MONTH_NAMES[idx]}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 justify-center">
              <span className="flex items-center gap-1 text-[10px] text-[#4A5568]">
                <span className="w-3 h-3 bg-blue-400 rounded-sm" /> {t('accountingDashboard.revenue')}
              </span>
              <span className="flex items-center gap-1 text-[10px] text-[#4A5568]">
                <span className="w-3 h-3 bg-red-400 rounded-sm" /> {t('accountingDashboard.expenses')}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Document summary */}
        <Card className="border-[#E2E8F0]" data-testid="document-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
              {t('accountingDashboard.documentSummary')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-2.5 bg-[#F4F6F8] rounded-lg">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-blue-600" />
                <span className="text-sm text-[#0A0F1A]">{t('accountingDashboard.invoices')}</span>
              </div>
              <span className="font-bold text-sm text-[#0033A0]">{stats.fatturato.totale_fatture}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-[#F4F6F8] rounded-lg">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-indigo-600" />
                <span className="text-sm text-[#0A0F1A]">{t('accountingDashboard.proformaWaiting')}</span>
              </div>
              <div className="text-right">
                <span className="font-bold text-sm text-indigo-600">{stats.proforma.in_attesa}</span>
                <p className="text-[9px] text-[#4A5568]">{formatCurrency(stats.proforma.valore_attesa)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-[#F4F6F8] rounded-lg">
              <div className="flex items-center gap-2">
                <ShoppingCart size={18} className="text-red-600" />
                <span className="text-sm text-[#0A0F1A]">{t('accountingDashboard.purchases')}</span>
              </div>
              <span className="font-bold text-sm text-red-600">{stats.acquisti.totale_acquisti}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-[#F4F6F8] rounded-lg">
              <div className="flex items-center gap-2">
                <Truck size={18} className="text-orange-600" />
                <span className="text-sm text-[#0A0F1A]">{t('accountingDashboard.ddtInTransit')}</span>
              </div>
              <span className="font-bold text-sm text-orange-600">{stats.ddt.in_transito}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-[#F4F6F8] rounded-lg">
              <div className="flex items-center gap-2">
                <ArrowCounterClockwise size={18} className="text-red-600" />
                <span className="text-sm text-[#0A0F1A]">{t('accountingDashboard.creditNotes')}</span>
              </div>
              <span className="font-bold text-sm text-red-600">{formatCurrency(stats.note_credito.totale)}</span>
            </div>
            {stats.passive.da_registrare > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <Receipt size={18} className="text-amber-600" />
                  <span className="text-sm text-amber-800">{t('accountingDashboard.passiveToRegister')}</span>
                </div>
                <span className="font-bold text-sm text-amber-700">{stats.passive.da_registrare}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Purchases by category */}
      {Object.keys(stats.acquisti.per_categoria).length > 0 && (
        <Card className="border-[#E2E8F0]" data-testid="purchases-by-category">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
              {t('accountingDashboard.purchasesByCategory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(stats.acquisti.per_categoria).map(([cat, val]) => (
                <div key={cat} className="p-3 bg-[#F4F6F8] rounded-lg text-center">
                  <p className="text-xs text-[#4A5568] capitalize">{t(`purchase.categories.${cat}`) || cat}</p>
                  <p className="font-bold text-sm text-[#0A0F1A] mt-1">{formatCurrency(val)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly detail */}
      <Card className="border-[#E2E8F0]" data-testid="monthly-detail">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('accountingDashboard.monthlyDetail')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-2 text-xs text-[#4A5568] font-medium">{t('accountingDashboard.month')}</th>
                  <th className="text-right py-2 text-xs text-[#4A5568] font-medium">{t('accountingDashboard.revenue')}</th>
                  <th className="text-right py-2 text-xs text-[#4A5568] font-medium">{t('accountingDashboard.expenses')}</th>
                  <th className="text-right py-2 text-xs text-[#4A5568] font-medium">{t('accountingDashboard.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {(stats.andamento_mensile || []).filter(m => m.fatturato > 0 || m.acquisti > 0).map((m, idx) => {
                  const monthIdx = parseInt(m.month.split('-')[1]) - 1;
                  const margin = m.fatturato - m.acquisti;
                  return (
                    <tr key={m.month} className="border-b border-[#F4F6F8]">
                      <td className="py-2 text-[#0A0F1A]">{MONTH_NAMES[monthIdx]} {m.month.split('-')[0]}</td>
                      <td className="py-2 text-right text-blue-600 font-medium">{formatCurrency(m.fatturato)}</td>
                      <td className="py-2 text-right text-red-600 font-medium">{formatCurrency(m.acquisti)}</td>
                      <td className={`py-2 text-right font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(margin)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

